// /diaries/new — W2 §6.3 — write a new diary (returning user flow).
//
// Distinct from /onboarding/first-diary:
//   - No OnboardingShell stepper (you're not onboarding anymore)
//   - No "寫下第一篇日記" hero (this isn't your first one)
//   - Plain "新增日記" PageHero + DiaryEditor in a Card
//
// W3.1 — reads `#source=anniversary&anniversary_id=<uuid>` from the URL hash
// and forwards it to DiaryEditor so TodayBanner / AnniversaryDiaryList CTA
// can deep-link a pre-filled source.

import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api/pair";

import { PageHero } from "@/components/primitives/PageHero";
import { FormErrorBanner } from "@/components/auth/FormField";
import { DiaryEditor } from "@/components/diary/DiaryEditor";
import { useHidePillNav } from "@/components/system/BottomChrome";
import { createDiary, type DiaryCreateInput, type DiaryUpdateInput } from "@/lib/api/diary";
import { diaryErrorMessage } from "@/lib/i18n/diary";
import { queryKeys } from "@/lib/queries/keys";

export function NewDiaryPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  // W35.6: the docked save action owns the bottom edge (see BottomChrome).
  useHidePillNav();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Parse deep-link hash params for anniversary source pre-fill.
  const initialAnniversaryId = useMemo(() => {
    if (typeof window === "undefined") return null;
    const hash = window.location.hash.slice(1);
    if (!hash) return null;
    const params = new URLSearchParams(hash);
    if (params.get("source") !== "anniversary") return null;
    return params.get("anniversary_id");
  }, []);

  const onSubmit = async (payload: DiaryCreateInput | DiaryUpdateInput) => {
    setBusy(true);
    setError(null);
    try {
      const diary = await createDiary(payload as DiaryCreateInput);
      void qc.invalidateQueries({ queryKey: queryKeys.diary.all });
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      // Clear the hash after a successful create so reload doesn't reapply.
      if (typeof window !== "undefined" && window.location.hash) {
        window.history.replaceState(null, "", window.location.pathname);
      }
      nav(`/diaries/${diary.id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(diaryErrorMessage(err.code, err.message));
      else setError("日記建立失敗, 請稍後再試");
    } finally {
      setBusy(false);
    }
  };

  return (
    // W35.6: this page docks the save action, so it owns the bottom edge —
    // `useHidePillNav()` suppresses the PillNav and the clearance it needed is
    // no longer required.
    //
    // W51 (D-5): the four wrapper layers (`PageContainer` + `SectionStack` +
    // `Card` + `div.p-s-6`) are gone. `DiaryEditor` **owns** this shape — it
    // carries its own `PageContainer`, `SectionStack`, dock bar and `pb-32`
    // clearance — so the page was nesting a second copy of a shape that was
    // already correct. Measured at 390px: the first field sat 57px from the
    // screen edge (16 + 1 border + 24 + 16) instead of 16px, and the field was
    // 276px wide instead of 358px. **22% of the width, for a duplicate.**
    //
    // There is deliberately no `pb-*` on this root either — `DiaryEditor`
    // brings its own clearance for its bar, and stacking padding is the same
    // mistake one level up.
    <div data-testid="new-diary-page" className="flex flex-col">
      <PageHero
        // W52.0 D-9 — the composer had no trail. The bottom action bar's back
        // (`DiaryEditor`'s `back` prop below) cancels the draft; this is the
        // way **up a level**, and the two were the only two exits, with only
        // one of them on the screen's title row.
        crumbs={[
          { to: "/diaries", label: "日記", testId: "diaries-new-back" },
          { label: "新增" },
        ]}
        eyebrow="建立"
        title="新增日記"
        subtitle="把今天的感受寫下來"
      />
      <FormErrorBanner message={error} />
      <DiaryEditor
        mode="create"
        busy={busy}
        initialAnniversaryId={initialAnniversaryId}
        onSubmit={onSubmit}
        back={{
          label: "返回日記列表",
          onClick: () => nav("/diaries"),
          testId: "diary-new-back",
        }}
      />
    </div>
  );
}
